import React from 'react';
import { CheckCircle, ArrowLeft, Send } from 'lucide-react';
import { CertifiedNetworkApplication } from '../../services/certifiedNetworkService';
import LoadingButton from '../LoadingButton';

interface Step7ReviewProps {
  data: Partial<CertifiedNetworkApplication>;
  associateData: any;
  onSubmit: () => void;
  onBack: () => void;
  loading: boolean;
}

const Step7Review: React.FC<Step7ReviewProps> = ({ 
  data, 
  associateData,
  onSubmit, 
  onBack, 
  loading 
}) => {
  return (
    <div className="space-y-8">
      <div className="text-center mb-6">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 dark:bg-green-900/50 rounded-full mb-4">
          <CheckCircle className="w-8 h-8 text-green-600 dark:text-green-400" />
        </div>
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
          Riepilogo Domanda
        </h2>
        <p className="text-gray-600 dark:text-gray-300">
          Verifica i dati inseriti prima di inviare la domanda.
        </p>
      </div>

      {/* Riepilogo Sezioni */}
      <div className="space-y-4">
        {/* Dati Studio */}
        <ReviewSection title="Dati dello Studio">
          <ReviewItem label="Ragione Sociale" value={data.studioData?.ragioneSociale} />
          <ReviewItem label="Partita IVA" value={data.studioData?.partitaIVA} />
          <ReviewItem label="Codice Fiscale" value={data.studioData?.codiceFiscale} />
          <ReviewItem label="Email" value={data.studioData?.email} />
          <ReviewItem label="Telefono" value={data.studioData?.telefono} />
          <ReviewItem label="Sito Web" value={data.studioData?.sitoWeb} />
          <ReviewItem
            label="Sede Legale"
            value={data.studioData?.sedeLegale ?
              `${data.studioData.sedeLegale.indirizzo}, ${data.studioData.sedeLegale.cap} ${data.studioData.sedeLegale.citta} (${data.studioData.sedeLegale.provincia})`
              : '-'
            }
          />
          <ReviewItem
            label="Sede Operativa"
            value={data.studioData?.sedeOperativa ?
              `${data.studioData.sedeOperativa.indirizzo}, ${data.studioData.sedeOperativa.cap} ${data.studioData.sedeOperativa.citta} (${data.studioData.sedeOperativa.provincia})`
              : '-'
            }
          />
          <ReviewItem label="Numero Dipendenti" value={data.studioData?.numeroDipendenti?.toString()} />
          <ReviewItem label="Totale Addetti" value={data.studioData?.totaleAddetti?.toString()} />
        </ReviewSection>

        {/* Legale Rappresentante */}
        <ReviewSection title="Legale Rappresentante">
          <ReviewItem
            label="Nome Completo"
            value={`${data.legalRepresentative?.nome || ''} ${data.legalRepresentative?.cognome || ''}`.trim()}
          />
          <ReviewItem label="Codice Fiscale" value={data.legalRepresentative?.codiceFiscale} />
          <ReviewItem label="Ruolo" value={data.legalRepresentative?.ruolo} />
          <ReviewItem label="Numero Tessera UNASCA" value={data.legalRepresentative?.numeroTesseraUNASCA} />
        </ReviewSection>

        {/* Sermetra */}
        <ReviewSection title="Consorzio Sermetra">
          <ReviewItem
            label="Consorziato"
            value={data.sermetra?.isConsorziato ? '✓ Sì' : '✗ No'}
          />
          {data.sermetra?.isConsorziato && (
            <ReviewItem label="Numero Consorziato" value={data.sermetra?.numeroConsorziato} />
          )}
        </ReviewSection>

        {/* DPO */}
        <ReviewSection title="Data Protection Officer (DPO)">
          <ReviewItem
            label="DPO Nominato"
            value={data.dpo?.isNominato ? '✓ Sì' : '✗ No'}
          />
          {data.dpo?.isNominato && (
            <>
              <ReviewItem label="Nome DPO" value={data.dpo?.nomeCompleto} />
              <ReviewItem label="Descrizione Protezione Dati" value={data.dpo?.descrizioneProtezione} />
            </>
          )}
        </ReviewSection>

        {/* GDPR */}
        <ReviewSection title="Conformità GDPR">
          <ReviewItem
            label="Misure GDPR Adottate"
            value={data.gdpr?.hasMisure ? '✓ Sì' : '✗ No'}
          />
          <ReviewItem
            label="DPIA Eseguita"
            value={data.gdpr?.hasDPIA ? '✓ Sì' : '✗ No'}
          />
        </ReviewSection>

        {/* Assicurazione */}
        <ReviewSection title="Assicurazione Professionale">
          <ReviewItem
            label="Assicurazione Attiva"
            value={data.insurance?.hasInsurance ? '✓ Sì' : '✗ No'}
          />
          {data.insurance?.hasInsurance && (
            <>
              <ReviewItem
                label="Polizza Sermetra"
                value={data.insurance?.isSermetra ? '✓ Sì' : '✗ No'}
              />
              <ReviewItem label="Compagnia" value={data.insurance?.compagnia} />
              <ReviewItem label="Numero Polizza" value={data.insurance?.numeroPolizza} />
              <ReviewItem label="Massimale" value={data.insurance?.massimale} />
            </>
          )}
        </ReviewSection>

        {/* Cybersecurity */}
        <ReviewSection title="Cybersecurity">
          <ReviewItem
            label="Misure di Sicurezza Adottate"
            value={data.cybersecurity?.hasMisure ? '✓ Sì' : '✗ No'}
          />
          {data.cybersecurity?.hasMisure && (
            <ReviewItem label="Descrizione Soluzioni" value={data.cybersecurity?.descrizioneSoluzioni} />
          )}
        </ReviewSection>

        {/* Pre-Autorizzazioni */}
        <ReviewSection title="Pre-Autorizzazioni">
          <div className="text-sm text-gray-700 dark:text-gray-300">
            <strong>{data.preAuthorizations?.filter(p => p.selected).length || 0}</strong> materie selezionate
            {(data.preAuthorizations?.filter(p => p.selected).length || 0) > 0 && (
              <ul className="list-disc list-inside mt-2 ml-2 space-y-1">
                {data.preAuthorizations?.filter(p => p.selected).map((p, i) => (
                  <li key={i}>{p.materia}</li>
                ))}
              </ul>
            )}
          </div>
        </ReviewSection>

        {/* Servizi */}
        <ReviewSection title="Servizi Offerti">
          <div className="text-sm text-gray-700 dark:text-gray-300">
            <strong>{data.services?.length || 0}</strong> servizi selezionati
            {(data.services?.length || 0) > 0 && (
              <ul className="list-disc list-inside mt-2 ml-2 space-y-1">
                {data.services?.map((s, i) => (
                  <li key={i}>{s}</li>
                ))}
              </ul>
            )}
          </div>
        </ReviewSection>

        {/* Formazione */}
        <ReviewSection title="Formazione">
          <ReviewItem
            label="Corsi Frequentati"
            value={data.training?.hasFrequentato ? '✓ Sì' : '✗ No'}
          />
          {data.training?.hasFrequentato && (
            <ReviewItem
              label="Documenti Caricati"
              value={`${data.training?.documenti?.length || 0} file`}
            />
          )}
        </ReviewSection>

        {/* EBAS */}
        <ReviewSection title="Diritti EBAS">
          <ReviewItem
            label="Versa Diritti EBAS"
            value={data.ebas?.versaDiritti ? '✓ Sì' : '✗ No'}
          />
        </ReviewSection>

        {/* Obblighi */}
        <ReviewSection title="Obblighi di Collaborazione">
          <ReviewItem
            label="Accettati"
            value={data.collaborationObligations?.accepted ? '✓ Sì' : '✗ No'}
          />
        </ReviewSection>

        {/* Dichiarazione */}
        <ReviewSection title="Dichiarazione di Veridicità">
          <ReviewItem label="Firmatario" value={data.declaration?.signerName} />
          <ReviewItem
            label="Confermata"
            value={data.declaration?.accepted ? '✓ Sì' : '✗ No'}
          />
        </ReviewSection>
      </div>

      {/* Warning */}
      <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700 rounded-lg p-4">
        <p className="text-sm text-yellow-800 dark:text-yellow-200">
          <strong>Attenzione:</strong> Una volta inviata, la domanda non potrà più essere modificata
          e sarà sottoposta alla valutazione del Comitato di Gestione della Rete Certificata UNASCA.
        </p>
      </div>

      {/* Buttons */}
      <div className="flex justify-between pt-6 border-t border-gray-200 dark:border-gray-700">
        <button
          type="button"
          onClick={onBack}
          disabled={loading}
          className="flex items-center gap-2 px-6 py-2 text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white disabled:opacity-50"
        >
          <ArrowLeft className="w-5 h-5" />
          Indietro
        </button>
        <LoadingButton
          onClick={onSubmit}
          loading={loading}
          className="flex items-center gap-2 bg-green-600 dark:bg-green-500 text-white px-8 py-3 rounded-lg hover:bg-green-700 dark:hover:bg-green-600 text-lg font-semibold"
        >
          <Send className="w-5 h-5" />
          Invia Domanda
        </LoadingButton>
      </div>
    </div>
  );
};

// Componenti helper
interface ReviewSectionProps {
  title: string;
  children: React.ReactNode;
}

const ReviewSection: React.FC<ReviewSectionProps> = ({ title, children }) => (
  <div className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
    <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3 uppercase tracking-wide">{title}</h3>
    <div className="space-y-2">
      {children}
    </div>
  </div>
);

interface ReviewItemProps {
  label: string;
  value?: string;
}

const ReviewItem: React.FC<ReviewItemProps> = ({ label, value }) => (
  <div className="flex justify-between text-sm gap-4">
    <span className="text-gray-600 dark:text-gray-400 flex-shrink-0">{label}:</span>
    <span className="text-gray-900 dark:text-white font-medium text-right">{value || '-'}</span>
  </div>
);

export default Step7Review;

